import React, { useContext, useRef, useEffect } from 'react';
import { Button, Input, Radio, Space } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { ThemeContext } from '../contexts/ThemeContext';

export interface IColumnSearchFilterProps {
    title?: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    onSearch: (value: string | null) => void;
    onClear?: () => void;
    isActive?: boolean;
    formatter?: (value: string) => string;
    width?: number;
}

export interface IColumnRadioFilterOption {
    label: string;
    value: string;
}

export interface IColumnRadioFilterProps {
    title?: string;
    value: string;
    onChange: (value: string) => void;
    onClear?: () => void;
    isActive?: boolean;
    options: IColumnRadioFilterOption[];
    width?: number;
}

function SearchFilterDropdown({
    title,
    placeholder,
    value,
    onChange,
    onSearch,
    onClear,
    formatter,
    width,
    confirm,
    clearFilters,
    isDark,
}: IColumnSearchFilterProps & { confirm: () => void; clearFilters?: () => void; isDark: boolean }) {
    const inputRef = useRef<any>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div style={{ padding: 12, width: width || 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {title ? (
                <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                    {title}
                </div>
            ) : null}
            <Input
                ref={inputRef}
                placeholder={placeholder || 'Digite...'}
                value={value}
                onChange={(e) => {
                    const val = formatter ? formatter(e.target.value) : e.target.value;
                    onChange(val);
                }}
                onPressEnter={() => {
                    const trimmed = value.trim();
                    onSearch(trimmed || null);
                    confirm();
                }}
                allowClear
            />
            <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    size="small"
                    onClick={() => {
                        const trimmed = value.trim();
                        onSearch(trimmed || null);
                        confirm();
                    }}
                    style={{ minWidth: 100 }}
                >
                    Pesquisar
                </Button>
                <Button
                    size="small"
                    onClick={() => {
                        onChange('');
                        onSearch(null);
                        onClear?.();
                        if (clearFilters) clearFilters();
                        confirm();
                    }}
                    style={{ minWidth: 80 }}
                >
                    Limpar
                </Button>
            </Space>
        </div>
    );
}

function RadioFilterDropdown({
    title,
    value,
    onChange,
    onClear,
    options,
    width,
    confirm,
    clearFilters,
    isDark,
}: IColumnRadioFilterProps & { confirm: () => void; clearFilters?: () => void; isDark: boolean }) {
    return (
        <div style={{ padding: 12, width: width || 230, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {title ? (
                <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                    {title}
                </div>
            ) : null}
            <Radio.Group
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    confirm();
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
                {options.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>
                        {opt.label}
                    </Radio>
                ))}
            </Radio.Group>
            <Button
                size="small"
                onClick={() => {
                    onClear?.();
                    if (clearFilters) clearFilters();
                    confirm();
                }}
                style={{ marginTop: 4 }}
            >
                Limpar
            </Button>
        </div>
    );
}

export default function useTableColumnFilter() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const getColumnSearchProps = (props: IColumnSearchFilterProps) => {
        return {
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <SearchFilterDropdown
                    {...props}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    isDark={isDark}
                />
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: props.isActive ? '#1677ff' : undefined }} />
            ),
        };
    };

    const getColumnRadioProps = (props: IColumnRadioFilterProps) => {
        return {
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <RadioFilterDropdown
                    {...props}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    isDark={isDark}
                />
            ),
            filterIcon: () => (
                <FilterOutlined style={{ color: props.isActive ? '#1677ff' : undefined }} />
            ),
        };
    };

    return {
        getColumnSearchProps,
        getColumnRadioProps,
        isDark,
    };
}
